import { action, computed, observable, makeAutoObservable } from 'mobx';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { UploadImageState } from 'src/AppState/UploadImageState/UploadImageState';
import { LiveNotificationsModelType } from 'src/api/config/live_notifications/get_live_notifications';
import * as t from 'io-ts';
import { createGuard } from 'src_common/common/createGuard';
import { TrpcClient } from 'src/AppState/TrpcClient';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { FormModel } from 'src_common/common/mobx-utils/Form2/FormModel';
import { validateNotEmpty } from 'src_common/common/mobx-utils/Form2/validators';
import { validateLength, validateListOfNumbersWithLimit } from 'src/api/utils/form2Validators';
import { AutoMap } from 'src_common/common/mobx-utils/AutoMap';
import { LiveNotificationsItemState } from './LiveNotificationsItemState';
import { validateIsNumber } from 'src/api/utils/form2Validators/validateIsNumber';

const NotificationDetailsIO = t.interface({
    idsList: t.array(t.number),
});

export type PromoNotificationsType = t.TypeOf<typeof NotificationDetailsIO>;

const isNotificationDetails = createGuard(NotificationDetailsIO);

interface LiveNotificationsLoading {
    type: 'loading';
}

interface LiveNotificationsReady {
    type: 'ready';
    value: Array<LiveNotificationsModelType>;
}

interface LiveNotificationsError {
    type: 'error';
    value: string;
}

type LiveNotificationsContentResponse = LiveNotificationsLoading | LiveNotificationsReady | LiveNotificationsError;

export interface LiveNotificationType {
    title: string;
    is_active: boolean;
    cta_game: string;
    cta_text: string;
    cta_event: string;
    message_text: string;
    visibility_option: string;
    cta_html_link: string;
    users_ids: string;
    date_from: string;
    date_to: string;
}

export class LiveNotificationsState {
    @observable public readonly liveNotificationsResource: Resource<Array<LiveNotificationsModelType> | null>;
    @observable public editedLiveNotification: LiveNotificationsModelType | null = null;
    @observable public isLoading: boolean = false;
    @observable public ctaError: string | null = null;
    @observable public showForAll: boolean = false;
    @observable public isEditMode: boolean = false;

    public readonly title: FormInputState<string, string>;
    public readonly message: FormInputState<string, string>;
    public readonly ctaText: FormInputState<string, string>;
    public readonly imgUrl: FormInputState<string, string>;
    public readonly ctaHtmlLinkUrl: FormInputState<string, string>;
    public readonly multipleTimes: FormInputState<boolean, boolean>;
    public readonly isActive: FormInputState<boolean, boolean>;
    public readonly ctaGameLink: FormInputState<string, string>;
    public readonly ctaEventId: FormInputState<string, string>;
    public readonly dateFrom: FormInputState<string, string>;
    public readonly dateTo: FormInputState<string, string>;
    public readonly visibilityOption: FormInputState<string, string>;
    public readonly idsList: FormInputState<string, string>;

    public readonly form: FormModel<LiveNotificationType>;

    public uploadImageState: UploadImageState;

    public readonly items: AutoMap<number, LiveNotificationsItemState>;

    public constructor(private readonly trpcClient: TrpcClient) {
        this.uploadImageState = new UploadImageState('live-notifications');

        this.liveNotificationsResource = new Resource(async (): Promise<Array<LiveNotificationsModelType> | null> => {
            this.isLoading = true;

            const response = await this.trpcClient.client.liveNotificationsRouter.getLiveNotifications.query();

            this.isLoading = false;
            // @ts-expect-error LT-2242 - remove when task is done
            return response.body ?? null;
        });

        this.title = FormInputState.new('').map(validateLength(30)).map(validateNotEmpty('Title is required'));

        this.isActive = FormInputState.new<boolean>(false);

        this.multipleTimes = FormInputState.new<boolean>(false);

        this.message = FormInputState.new('').map(validateLength(200));

        this.ctaText = FormInputState.new('');

        this.imgUrl = FormInputState.new('');

        this.ctaHtmlLinkUrl = FormInputState.new('');

        this.ctaGameLink = FormInputState.new('');

        this.ctaEventId = FormInputState.new('').map(validateIsNumber);

        this.dateFrom = FormInputState.new('').map(validateNotEmpty('Start date is required'));

        this.dateTo = FormInputState.new('').map(validateNotEmpty('End date is required'));

        this.visibilityOption = FormInputState.new('').map(validateNotEmpty('This field is required'));

        this.idsList = FormInputState.new('').map(validateListOfNumbersWithLimit(2000));

        this.form = FormModel.group({
            title: this.title,
            is_active: this.isActive,
            cta_game: this.ctaGameLink,
            cta_text: this.ctaText,
            img_url: this.imgUrl,
            cta_event: this.ctaEventId,
            message_text: this.message,
            visibility_option: this.visibilityOption,
            cta_html_link: this.ctaHtmlLinkUrl,
            users_ids: this.idsList,
            date_from: this.dateFrom,
            date_to: this.dateTo,
        });

        this.items = new AutoMap((id: number) => {
            const getItem = (): LiveNotificationsModelType | null =>
                this.liveNotificationsSorted.find((item) => item.id === id) ?? null;
            return new LiveNotificationsItemState(id, getItem, this, trpcClient);
        });
        makeAutoObservable(this);
    }

    @action public resetForm = (): void => {
        this.editedLiveNotification = null;
        this.ctaError = null;
        this.showForAll = false;
        this.isEditMode = false;
        this.form.reset();
        this.uploadImageState.handleResetImage();
    };

    @action public setShowForAll = (value: boolean): void => {
        this.showForAll = value;
    };

    @computed public get addedIds(): Array<string> {
        return this.idsList.value.split(/[,|\n]+/).filter((elem) => elem !== '');
    }

    @computed public get prepareIds(): PromoNotificationsType {
        const tempDetails: PromoNotificationsType = {
            idsList: [],
        };
        const idsList = this.idsList.value;
        if (idsList === '') {
            return tempDetails;
        } else {
            const idsListParsed = idsList
                .split(/[,|\n]+/)
                .map((elem) => parseInt(elem, 10))
                .filter((elem) => !isNaN(elem));
            tempDetails.idsList = idsListParsed;

            return tempDetails;
        }
    }

    public checkifCtaIsValid = (cta_url_link: string, cta_game: string, cta_event_id: string): boolean => {
        if (cta_url_link === '' && cta_game === '' && cta_event_id === '') {
            return false;
        }
        return true;
    };

    public checkifShowOptionsIsValid = (users_ids: Array<number>, showForAll: boolean): boolean => {
        if ((showForAll && users_ids.length > 0) || (users_ids.length === 0 && showForAll === false)) {
            return false;
        }
        return true;
    };

    @action public createLiveNotification = async (): Promise<void> => {
        this.form.setAsVisited();
        const { result } = this.form;

        if (result.value.type === 'ok') {
            const isCtaNotFilled = this.checkifCtaIsValid(
                result.value.data.cta_html_link,
                result.value.data.cta_game,
                result.value.data.cta_event
            );
            if (isCtaNotFilled === false) {
                this.ctaError = 'Please provide call to action game, link or event id';
                this.isLoading = false;
                return;
            }

            const isShowNotFilled = this.checkifShowOptionsIsValid(this.prepareIds.idsList, this.showForAll);
            if (isShowNotFilled === false) {
                this.ctaError = 'Please select All authorized customers option or provide some users ids';
                this.isLoading = false;
                return;
            }
            await this.liveNotificationsResource.refresh();

            const topElem = this.liveNotificationsSorted[0] ?? null;
            const display_order = topElem === null ? 1 : topElem.display_order + 1;

            const data = {
                ...result.value.data,
                img_url: this.uploadImageState.imgUrl,
                users_ids: this.prepareIds,
                is_show_for_all_users: this.showForAll,
                display_order: display_order,
            };

            const submitResult = await this.trpcClient.client.liveNotificationsRouter.createLiveNotification.mutate({
                data,
            });

            if (submitResult === null) {
                console.error('Error');
            } else {
                await this.liveNotificationsResource.refresh();
                this.resetForm();
            }
        }
        makeAutoObservable(this);
    };

    @action public updateLiveNotification = async (): Promise<void> => {
        this.form.setAsVisited();
        const result = this.form.result;

        if (result.value.type === 'ok' && this.editedLiveNotification !== null) {
            const isCtaNotFilled = this.checkifCtaIsValid(
                result.value.data.cta_html_link,
                result.value.data.cta_game,
                result.value.data.cta_event
            );
            if (isCtaNotFilled === false) {
                this.ctaError = 'Please provide call to action game, link or event id';
                this.isLoading = false;
                return;
            }

            const isShowNotFilled = this.checkifShowOptionsIsValid(this.prepareIds.idsList, this.showForAll);
            if (isShowNotFilled === false) {
                this.ctaError = 'Please select All authorized customers option or provide some users ids';
                this.isLoading = false;
                return;
            }
            const liveNotificationData = {
                ...result.value.data,
                img_url: this.uploadImageState.imgUrl,
                users_ids: this.prepareIds,
                is_show_for_all_users: this.editedLiveNotification.is_show_for_all_users,
                display_order: this.editedLiveNotification.display_order,
            };

            try {
                await this.trpcClient.client.liveNotificationsRouter.editLiveNotification.mutate({
                    data: liveNotificationData,
                    id: this.editedLiveNotification.id,
                });
                this.resetForm();
                await this.liveNotificationsResource.refresh();
            } catch (error) {
                console.error(error);
            }
        }
    };

    @action public setEditedLiveNotification = async (liveNotification: LiveNotificationsModelType): Promise<void> => {
        this.isEditMode = true;
        this.editedLiveNotification = liveNotification;
        this.title.setValue(liveNotification.title);
        this.isActive.setValue(liveNotification.is_active);
        this.ctaGameLink.setValue(liveNotification.cta_game ?? '');
        this.ctaHtmlLinkUrl.setValue(liveNotification.cta_html_link ?? '');
        this.ctaText.setValue(liveNotification.cta_text ?? '');
        this.message.setValue(liveNotification.message_text ?? '');
        this.dateFrom.setValue(liveNotification.date_from ?? '');
        this.dateTo.setValue(liveNotification.date_to ?? '');
        this.visibilityOption.setValue(liveNotification.visibility_option ?? '');
        this.ctaText.setValue(liveNotification.cta_text ?? '');
        this.ctaEventId.setValue(liveNotification.cta_event ?? '');
        this.uploadImageState.handleChooseImage(liveNotification.img_url ?? '');

        if (liveNotification.users_ids === undefined) {
            this.setShowForAll(true);
        }

        if (isNotificationDetails(liveNotification.users_ids)) {
            if (liveNotification.users_ids.idsList.length === 0) {
                this.setShowForAll(true);
            }
            this.idsList.setValue(liveNotification.users_ids.idsList.toString());
        }
    };

    @computed private get liveNotifications(): LiveNotificationsContentResponse {
        const liveNotifications = this.liveNotificationsResource.get();
        if (liveNotifications.type === 'ready' && liveNotifications.value !== null) {
            return {
                type: 'ready',
                value: liveNotifications.value,
            };
        }
        if (liveNotifications.type === 'loading') {
            return {
                type: 'loading',
            };
        }

        return {
            type: 'error',
            value: 'Something went wrong with fetching landing page info',
        };
    }

    @computed public get liveNotificationsSorted(): Array<LiveNotificationsModelType> {
        if (this.liveNotifications.type === 'ready') {
            return this.liveNotifications.value.sort(
                (lp1: LiveNotificationsModelType, lp2: LiveNotificationsModelType) => {
                    if (lp1 !== null && lp2 !== null) {
                        if (lp1.display_order > lp2.display_order) {
                            return -1;
                        }
                        if (lp1.display_order < lp2.display_order) {
                            return 1;
                        }
                        return 0;
                    }
                    return -1;
                }
            );
        }
        return [];
    }

    @computed public get isFormValid(): boolean {
        const { result } = this.form;
        return result.value.type === 'ok';
    }
}
